import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

export default class extends Controller {
  static targets = ["portfolioValueChart", "equityChart", "cashFlowChart", "allocationChart", "debtDistributionChart", "equityProjectionChart", "amortizationChart", "valueHistoryChart", "tab", "tabPanel"]
  static values = { activeTab: { type: String, default: "properties" } }

  connect() {
    this.showTab(this.activeTabValue)
    if (this.hasPortfolioValueChartTarget) this.renderPortfolioValueChart()
    if (this.hasEquityChartTarget) this.renderEquityChart()
    if (this.hasCashFlowChartTarget) this.renderCashFlowChart()
    if (this.hasAllocationChartTarget) this.renderAllocationChart()
    if (this.hasDebtDistributionChartTarget) this.renderDebtDistributionChart()
    if (this.hasEquityProjectionChartTarget) this.renderEquityProjectionChart()
    if (this.hasAmortizationChartTarget) this.renderAmortizationChart()
    if (this.hasValueHistoryChartTarget) this.renderValueHistoryChart()
  }

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    this.activeTabValue = tab
    this.showTab(tab)
  }

  showTab(tab) {
    this.tabTargets.forEach(el => {
      el.classList.toggle("pf-tab--active", el.dataset.tab === tab)
    })
    this.tabPanelTargets.forEach(el => {
      el.hidden = el.dataset.tab !== tab
    })

    // Charts need a resize after being shown since they were hidden
    if (tab === "performance") {
      setTimeout(() => window.dispatchEvent(new Event("resize")), 50)
    }
  }

  renderPortfolioValueChart() {
    const data = JSON.parse(this.portfolioValueChartTarget.dataset.chartData)
    const todayIndex = data.findIndex(d => d.is_today)

    // Create point radius array: larger point for "today" marker
    const pointRadii = data.map((d, i) => d.is_today ? 7 : 3)
    const todayPointColors = data.map((d) => d.is_today ? "#1e40af" : "#3b82f6")

    // Vertical line annotation for "today"
    const todayLinePlugin = {
      id: "todayLine",
      afterDraw(chart) {
        if (todayIndex < 0) return
        const meta = chart.getDatasetMeta(0)
        if (!meta.data[todayIndex]) return
        const x = meta.data[todayIndex].x
        const ctx = chart.ctx
        const yAxis = chart.scales.y
        ctx.save()
        ctx.beginPath()
        ctx.setLineDash([4, 4])
        ctx.strokeStyle = "#64748b"
        ctx.lineWidth = 1.5
        ctx.moveTo(x, yAxis.top)
        ctx.lineTo(x, yAxis.bottom)
        ctx.stroke()
        ctx.setLineDash([])
        ctx.fillStyle = "#64748b"
        ctx.font = "11px system-ui, sans-serif"
        ctx.textAlign = "center"
        ctx.fillText("Today", x, yAxis.top - 6)
        ctx.restore()
      }
    }

    new Chart(this.portfolioValueChartTarget, {
      type: "line",
      data: {
        labels: data.map(d => d.date),
        datasets: [
          {
            label: "Portfolio Value",
            data: data.map(d => d.value),
            borderColor: "#3b82f6",
            backgroundColor: "rgba(59,130,246,0.08)",
            fill: true,
            tension: 0.3,
            pointRadius: pointRadii,
            pointBackgroundColor: todayPointColors,
            borderWidth: 2.5
          },
          {
            label: "Equity",
            data: data.map(d => d.equity),
            borderColor: "#6ABF4B",
            backgroundColor: "rgba(106,191,75,0.12)",
            fill: true,
            tension: 0.3,
            pointRadius: data.map(d => d.is_today ? 7 : 3),
            borderWidth: 2.5
          },
          {
            label: "Debt",
            data: data.map(d => d.debt),
            borderColor: "#ef4444",
            backgroundColor: "rgba(239,68,68,0.08)",
            fill: true,
            tension: 0.3,
            pointRadius: data.map(d => d.is_today ? 7 : 3),
            borderWidth: 2,
            borderDash: [5, 5]
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false
        },
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString()}`,
              title: (items) => {
                const idx = items[0].dataIndex
                const suffix = data[idx].is_today ? " (Today)" : ""
                return items[0].label + suffix
              }
            }
          }
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            ticks: { callback: (v) => "$" + (v / 1000).toFixed(0) + "k" },
            grid: { color: "#f1f5f9" }
          }
        }
      },
      plugins: [todayLinePlugin]
    })
  }

  renderEquityChart() {
    const data = JSON.parse(this.equityChartTarget.dataset.chartData)
    new Chart(this.equityChartTarget, {
      type: "bar",
      data: {
        labels: data.map(d => d.label),
        datasets: [
          {
            label: "Equity",
            data: data.map(d => d.equity),
            backgroundColor: "#6ABF4B",
            borderRadius: 4
          },
          {
            label: "Loan Balance",
            data: data.map(d => d.loan),
            backgroundColor: "#e2e8f0",
            borderRadius: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString()}`
            }
          }
        },
        scales: {
          x: { stacked: true, grid: { display: false } },
          y: {
            stacked: true,
            ticks: { callback: (v) => "$" + (v / 1000).toFixed(0) + "k" },
            grid: { color: "#f1f5f9" }
          }
        }
      }
    })
  }

  renderCashFlowChart() {
    const data = JSON.parse(this.cashFlowChartTarget.dataset.chartData)
    new Chart(this.cashFlowChartTarget, {
      type: "bar",
      data: {
        labels: data.map(d => d.label),
        datasets: [
          {
            label: "Income",
            data: data.map(d => d.income),
            backgroundColor: "#6ABF4B",
            borderRadius: 4
          },
          {
            label: "Expenses",
            data: data.map(d => d.expenses),
            backgroundColor: "#f87171",
            borderRadius: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString()}`
            }
          }
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            ticks: { callback: (v) => "$" + v.toLocaleString() },
            grid: { color: "#f1f5f9" }
          }
        }
      }
    })
  }

  renderAllocationChart() {
    const data = JSON.parse(this.allocationChartTarget.dataset.chartData)
    const colors = ["#6ABF4B", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"]
    new Chart(this.allocationChartTarget, {
      type: "doughnut",
      data: {
        labels: data.map(d => d.label),
        datasets: [{
          data: data.map(d => d.value),
          backgroundColor: colors.slice(0, data.length),
          borderWidth: 2,
          borderColor: "#fff"
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const total = ctx.dataset.data.reduce((a, b) => a + b, 0)
                const pct = ((ctx.parsed / total) * 100).toFixed(1)
                return `${ctx.label}: $${ctx.parsed.toLocaleString()} (${pct}%)`
              }
            }
          }
        }
      }
    })
  }

  renderDebtDistributionChart() {
    const data = JSON.parse(this.debtDistributionChartTarget.dataset.chartData)
    const colors = { "Traditional Bank": "#3b82f6", "Hard Money": "#f59e0b", "Private Money": "#8b5cf6" }
    const fallbackColors = ["#3b82f6", "#f59e0b", "#8b5cf6", "#ef4444", "#14b8a6", "#ec4899"]
    new Chart(this.debtDistributionChartTarget, {
      type: "doughnut",
      data: {
        labels: data.map(d => d.label),
        datasets: [{
          data: data.map(d => d.value),
          backgroundColor: data.map((d, i) => colors[d.label] || fallbackColors[i % fallbackColors.length]),
          borderWidth: 2,
          borderColor: "#fff"
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const total = ctx.dataset.data.reduce((a, b) => a + b, 0)
                const pct = ((ctx.parsed / total) * 100).toFixed(1)
                return `${ctx.label}: $${ctx.parsed.toLocaleString()} (${pct}%)`
              }
            }
          }
        }
      }
    })
  }

  renderEquityProjectionChart() {
    const data = JSON.parse(this.equityProjectionChartTarget.dataset.chartData)
    new Chart(this.equityProjectionChartTarget, {
      type: "line",
      data: {
        labels: data.map(d => `Year ${d.year}`),
        datasets: [
          {
            label: "Property Value",
            data: data.map(d => d.value),
            borderColor: "#3b82f6",
            backgroundColor: "rgba(59,130,246,0.1)",
            fill: false,
            tension: 0.3,
            pointRadius: 4
          },
          {
            label: "Equity",
            data: data.map(d => d.equity),
            borderColor: "#6ABF4B",
            backgroundColor: "rgba(106,191,75,0.15)",
            fill: true,
            tension: 0.3,
            pointRadius: 4
          },
          {
            label: "Loan Balance",
            data: data.map(d => d.balance),
            borderColor: "#94a3b8",
            backgroundColor: "rgba(148,163,184,0.1)",
            fill: false,
            tension: 0.3,
            pointRadius: 4,
            borderDash: [5, 5]
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString()}`
            }
          }
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            ticks: { callback: (v) => "$" + (v / 1000).toFixed(0) + "k" },
            grid: { color: "#f1f5f9" }
          }
        }
      }
    })
  }

  renderAmortizationChart() {
    const data = JSON.parse(this.amortizationChartTarget.dataset.chartData)
    // Sample every 12th month for yearly view
    const yearly = data.filter((_, i) => (i + 1) % 12 === 0 || i === 0)
    new Chart(this.amortizationChartTarget, {
      type: "line",
      data: {
        labels: yearly.map(d => `Mo ${d.month}`),
        datasets: [
          {
            label: "Loan Balance",
            data: yearly.map(d => d.balance),
            borderColor: "#ef4444",
            backgroundColor: "rgba(239,68,68,0.1)",
            fill: true,
            tension: 0.3,
            pointRadius: 3
          },
          {
            label: "Equity",
            data: yearly.map(d => d.equity),
            borderColor: "#6ABF4B",
            backgroundColor: "rgba(106,191,75,0.1)",
            fill: true,
            tension: 0.3,
            pointRadius: 3
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString()}`
            }
          }
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            ticks: { callback: (v) => "$" + (v / 1000).toFixed(0) + "k" },
            grid: { color: "#f1f5f9" }
          }
        }
      }
    })
  }

  renderValueHistoryChart() {
    const data = JSON.parse(this.valueHistoryChartTarget.dataset.chartData)
    if (!data.length) return

    const pointRadii = data.map(d => d.is_purchase || d.is_sale ? 6 : 0)
    const pointColors = data.map(d => {
      if (d.is_purchase) return "#B87933"
      if (d.is_sale) return "#A62E2E"
      return "#1F5A38"
    })
    const pointBorders = data.map(d => (d.is_purchase || d.is_sale) ? "#fff" : "transparent")

    new Chart(this.valueHistoryChartTarget, {
      type: "line",
      data: {
        labels: data.map(d => d.label),
        datasets: [
          {
            label: "Property Value",
            data: data.map(d => d.value),
            borderColor: "#1F5A38",
            backgroundColor: "rgba(31,90,56,0.12)",
            fill: true,
            tension: 0.25,
            borderWidth: 2,
            pointRadius: pointRadii,
            pointBackgroundColor: pointColors,
            pointBorderColor: pointBorders,
            pointBorderWidth: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const d = data[ctx.dataIndex]
                const tag = d.is_purchase ? " (Purchase)" : d.is_sale ? " (Sale)" : ""
                return `$${ctx.parsed.y.toLocaleString()}${tag}`
              }
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              maxTicksLimit: 12,
              autoSkip: true
            }
          },
          y: {
            ticks: { callback: (v) => "$" + (v / 1000).toFixed(0) + "k" },
            grid: { color: "#f1f5f9" }
          }
        }
      }
    })
  }
}
